<template>
  <div class=" relative rounded-2xl bg-white">
    <template v-if="!isConfirmed">
      <div class="relative flex items-center justify-center gap-x-1 border-b border-gray-100 px-6 py-3.5">
        <button
          v-if="hasPreviousStep"
          class="btn btn--text absolute left-6 p-1"
          type="button"
          @click="goToPreviousStep"
        >
          <IconArrowBackUp
            :size="24"
            aria-hidden="true"
          />
          <span
            class="max-lg:sr-only"
            v-text="'Previous Step'"
          />
        </button>

        <div class="flex flex-col items-center">
          <p
            class="wc-overline text-gray-800"
            v-text="`Step ${activeStepNumber} of ${TOTAL_STEP_COUNT}`"
          />
          <ol
            aria-hidden="true"
            class="flex  items-center gap-x-1.5"
          >
            <li
              v-for="step in TOTAL_STEP_COUNT"
              :key="`step-indicator-${step}`"
              :class="{
                'w-6 border-primary-500 bg-primary-500': activeStepNumber === step,
                'border-gray-500 bg-transparent': activeStepNumber !== step,
              }"
              class="block size-2 rounded-full border"
            />
          </ol>
        </div>
      </div>

      <component
        :is="activeStepComponent"
        v-if="!isLoading"
        v-bind="currentStepProps"
      />
    </template>

    <CheckoutConfirmation
      v-else
      :user="props.user"
      :order-number="orderNumber ?? ''"
    />

    <LoadingIndicator v-if="isLoading" />
  </div>
</template>

<script lang="ts" setup>
import { IconArrowBackUp } from '@tabler/icons-vue'
import { computed, markRaw, onBeforeMount, ref, shallowRef, watch } from 'vue'

import LoadingIndicator from '@js/components/checkout/partials/LoadingIndicator.vue'
import CheckoutConfirmation from '@js/components/checkout/partials/CheckoutConfirmation.vue'
import StepBillingInformation from '@js/components/checkout/partials/StepBillingInformation.vue'
import StepPersonalInformation from '@js/components/checkout/partials/StepPersonalInformation.vue'

import { log } from '@js/utils/logger'
import { trackEvent } from '@js/utils/trackEvent'
import useAuth from '@js/composables/useAuth'
import { getCustomerRegistration } from '@js/api/customer/getCustomerRegistration'

import { Cart } from '@js/api/types/cart/Cart'
import { Customer } from '@js/api/types/customer/Customer'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'

import { setPaymentMethodOnCart } from '@js/api/cart/setPaymentMethodOnCart'
import { placeOrder } from '@js/api/checkout/placeOrder'

import { useRoute, useRouter } from 'vue-router'

import { useUserStore } from '@js/stores/userStore'

type StepComponent =
  | typeof StepPersonalInformation
  | typeof StepBillingInformation

const props = defineProps<{
  cart: Cart | null
  user: Customer | null
}>()

const emit = defineEmits<{
  cartUpdated: [cart: Cart | null]
  registrationUpdated: [id: number]
}>()

const TOTAL_STEP_COUNT = 2

const ACTIVE_STEP_MAP: Record<number, StepComponent> = {
  1: markRaw(StepPersonalInformation),
  2: markRaw(StepBillingInformation),
}

const { getLastRegistration, setLastRegistration } = useAuth()

const isLoading = ref(false)

const activeStepNumber = ref(1)
const activeStepComponent = shallowRef<StepComponent>(ACTIVE_STEP_MAP[1])

const registrationId = ref<number | null>(null)
const registration = ref<CustomerRegistration | null>(null)

const isLastStep = computed(() => activeStepNumber.value === TOTAL_STEP_COUNT)

const orderNumber = ref<string | null>(null)
const isConfirmed = ref(false)

const hasPreviousStep = computed(() => activeStepNumber.value > 1 && !isConfirmed.value)

const route = useRoute()
const router = useRouter()

const showConfirmation = (): void => {
  isConfirmed.value = true
  router.replace({
    path: route.path || '/checkout',
    query: { confirmed: 'true' },
  })
}
const userStore = useUserStore()

const internalCart = ref<Cart | null>(props.cart)

/**
 * Fires the same GTM events that Confirmation used to fire on mount.
 * Must be called before every redirect to the receipt page (paid and free orders).
 */
const trackOrderCompletion = (orderNum: string): void => {
  if (!props.user || !props.cart) return

  const item = props.cart.items[0]
  const sku = item.product.sku

  // Rounds to exactly 2 decimal places to avoid floating-point artifacts
  const toMoney = (value: number): number => Math.round(value * 100) / 100

  const grossRevenue = toMoney(item.prices.price.value)
  const couponDiscount = toMoney(props.cart.prices.discount?.amount.value ?? 0)
  const netRevenue = toMoney(grossRevenue + couponDiscount)

  trackEvent({
    event: 'transactionComplete',
    transactionId: orderNum,
    transactionAffiliation: 'Wound Care Education Institute',
    gross_revenue: grossRevenue,
    transactionTax: 0,
    transactionShipping: 0,
    coupon_code: props.cart.coupon_details?.code || '',
    coupon_discount: couponDiscount,
    net_revenue: netRevenue,
    transactionProducts: [
      {
        sku: sku,
        name: item.product.name,
        category: 'individual',
        price: item.prices.price.value,
        quantity: 1,
        coupon: '',
      },
    ],
  })

  if (sku.startsWith('WCEI-WEBINAR-')) {
    trackEvent({
      event: 'webinar_conversion',
      webinarSku: sku,
      orderNumber: orderNum,
    })
  }
}

const currentStepProps = computed(() => {
  if (activeStepNumber.value === 1) {
    return {
      user: props.user,
      cart: props.cart,
      registration: registration.value,
      registrationId: registrationId.value,
      onNextStep: goToNextStep,
      onUpdateRegistration: updateRegistration,
      onCartUpdated: (updatedCart: Cart) => {
        internalCart.value = updatedCart
        emit('cartUpdated', updatedCart)
      },
    } as const
  }

  return {
    cart: props.cart,
    user: props.user,
    registration: registration.value,
    onNextStep: (orderNum?: string) => {
      if (orderNum) {
        trackOrderCompletion(orderNum)
        orderNumber.value = orderNum
        showConfirmation()
      }
    },
  } as const
})

const goToPreviousStep = (): void => {
  if (activeStepNumber.value === 1) return

  const prevStep = activeStepNumber.value - 1
  const currentPath = route.path || window.location.pathname

  activeStepNumber.value = prevStep
  activeStepComponent.value = ACTIVE_STEP_MAP[prevStep]

  router.push({
    path: currentPath,
    query: { ...route.query, step: String(prevStep) }
  })
}

const goToNextStep = async (): Promise<void> => {
  if (isLastStep.value) return

  const price = props.cart?.prices.grand_total.value ?? 0
  const nextStep = activeStepNumber.value + 1
  const currentPath = route.path || window.location.pathname

  if (activeStepNumber.value === 1 && price === 0) {
    await handleFreeOrderPlacement()
    return
  }

  activeStepNumber.value = nextStep
  activeStepComponent.value = ACTIVE_STEP_MAP[nextStep]

  window.scrollTo({ top: 0, behavior: 'smooth' })

  router.push({
    path: currentPath,
    query: { ...route.query, step: String(nextStep) }
  })
}

const checkRegistration = async (): Promise<void> => {
  try {
    isLoading.value = true

    registrationId.value = getLastRegistration()

    if (registrationId.value) {
      registration.value = await getCustomerRegistration({
        registrationId: registrationId.value,
      })
    }
  } catch (error) {
    log({
      message: 'Error retrieving customer registration',
      file: 'src/wcei/js/components/checkout/partials/CheckoutForm.vue',
    })
  } finally {
    isLoading.value = false
  }
}

const updateRegistration = (customerRegistration: CustomerRegistration | null): void => {
  if (!customerRegistration) {
    log({
      message: 'Error updating customer registration',
      file: 'src/wcei/js/components/checkout/partials/CheckoutForm.vue',
    })

    return
  }

  registration.value = customerRegistration
  registrationId.value = customerRegistration?.registration_id ?? 0

  setLastRegistration(customerRegistration?.registration_id ?? 0)
  emit('registrationUpdated', customerRegistration?.registration_id)
}

const cartInitialized = ref(false)

const handleFreeOrderPlacement = async (): Promise<void> => {
  if (!props.cart || cartInitialized.value) return

  const price = props.cart?.prices.grand_total.value ?? 0

  if (price !== 0) return

  cartInitialized.value = true

  try {
    await setPaymentMethodOnCart({
      cartId: props.cart.id,
      paymentMethod: { code: 'free' },
    })

    const result = await placeOrder({
      quantity: 1,
      cartId: props.cart.id,
      sku: registration.value?.item.sku ?? '',
      registrationId: registration.value?.registration_id ?? 0,
      courseOption: 'individual',
      paying_by: 'myself',
    })

    orderNumber.value = String(result)

    if (orderNumber.value) {
      // Update incomplete registrations count in the global user store
      userStore.decrementIncompleteRegistrationsCount()
    }
    
    trackOrderCompletion(orderNumber.value)
    showConfirmation()
    
  } catch (error) {
    log({
      message: 'Error placing order for free product',
      file: 'src/wcei/js/components/checkout/partials/CheckoutForm.vue',
    })
  }
}

watch(() => route.query.step, (newStep) => {
  const stepNumber = Number(newStep)
  if (stepNumber && stepNumber in ACTIVE_STEP_MAP) {
    activeStepNumber.value = stepNumber
    activeStepComponent.value = ACTIVE_STEP_MAP[stepNumber]
  }
})

watch(() => props.cart, (newCart) => {
  internalCart.value = newCart
})

onBeforeMount(async () => {
  // Allow direct navigation to the confirmation view (e.g. after a page refresh on ?confirmed=true)
  if (route.query.confirmed === 'true') {
    isConfirmed.value = true
    await checkRegistration()
    return
  }

  const step = route.query.step || '1'

  if (Number(step) in ACTIVE_STEP_MAP) {
    activeStepNumber.value = Number(step)
    activeStepComponent.value = ACTIVE_STEP_MAP[Number(step)]
  }

  if (!route.query.step) {
    await router.push({
      path: '/checkout',
      query: { step: '1' },
    })
  }

  await checkRegistration()
})
</script>
